/**
 * The corner rank indices, drawn over the imported card art.
 *
 * ## Why the letters are not left to the SVG
 *
 * The deck in `public/cards/` is Byron Knoll's public-domain vector set. Every
 * pip, court figure and corner suit in it is real vector artwork — but the two
 * corner **rank letters** are `<text>` elements asking for `Arial` and
 * `Bitstream Vera Sans`.
 *
 * An SVG rasterised through an `Image` is a separate document. It cannot see
 * fonts loaded into the page with `FontFace`, and resolves only fonts installed
 * on the machine. Those letters would render in Arial on Windows, Helvetica on
 * macOS, and whatever DejaVu substitute a Linux box happens to pick — three
 * different decks, none of them ours, and the rank is the single most important
 * thing on a card to read.
 *
 * `cardTexture` strips the text and calls this instead, which draws the letters
 * in the game's own display face: the same type as the score, the pile totals
 * and the streak badge, identical on every platform.
 *
 * The corner **suit** pips are vector in the source and are kept — only the
 * letters are replaced.
 */

import type { CardId, Rank, Suit } from '@/game/types';

/**
 * Card ink, deliberately not the game palette.
 *
 * `palette.rose` is a UI signal for a bust; a pip is a printed red and must not
 * read as a game state. These match the deck's own artwork so a letter sits in
 * the same ink as the pip beneath it.
 */
const INK = {
  red: '#d40000',
  black: '#000000',
} as const;

const RED_SUITS: ReadonlySet<Suit> = new Set<Suit>(['h', 'd']);

export function suitOf(id: CardId): Suit {
  return id.slice(-1) as Suit;
}

export function rankOf(id: CardId): Rank {
  return id.slice(0, -1) as Rank;
}

/** What is printed in the corner. */
function indexLabel(rank: Rank): string {
  return rank === '10' ? '10' : rank.toUpperCase();
}

/**
 * Where the letter sits, as a fraction of the card.
 *
 * Matched to the artwork rather than chosen: in the source's 167.09 x 242.67
 * artboard the stripped text sat at a baseline near y 28 with the corner suit
 * pip directly beneath it, so the glyph's optical centre belongs around 8.7%
 * down. `x` centres it over that pip.
 */
const INDEX_X = 0.088;
const INDEX_Y = 0.087;
const INDEX_SIZE = 0.108;

function drawOne(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  rank: Rank,
  fontFamily: string,
): void {
  const size = h * INDEX_SIZE;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `${size}px ${fontFamily}`;

  const label = indexLabel(rank);
  ctx.save();
  ctx.translate(w * INDEX_X, h * INDEX_Y);
  // `10` is two glyphs in a slot cut for one, so it is condensed rather than
  // allowed to spill over the corner pip beneath it.
  if (label.length > 1) ctx.scale(0.74, 1);
  ctx.fillText(label, 0, 0);
  ctx.restore();
}

/**
 * Draw both corner indices into a `w` x `h` cell whose origin is its top-left.
 *
 * The second is the first rotated 180 degrees about the card's centre, which is
 * what lets a card be read from either end — and this game rotates cards on
 * entry and jitters them in the fan.
 */
export function drawCardIndices(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  id: CardId,
  fontFamily: string,
): void {
  const rank = rankOf(id);
  ctx.save();
  ctx.fillStyle = RED_SUITS.has(suitOf(id)) ? INK.red : INK.black;

  drawOne(ctx, w, h, rank, fontFamily);

  ctx.translate(w, h);
  ctx.rotate(Math.PI);
  drawOne(ctx, w, h, rank, fontFamily);

  ctx.restore();
}
